import React from 'react';
import styled from 'styled-components';
import {connect} from 'react-redux';
import StepHeader from '../StepHeader';
import {seatSelect} from '../../../actions/creators/checkout/seats';
import {prevStep} from '../../../actions/creators/checkout';
import {
  areCheckoutSeatsPreSelected,
  getCheckoutSeatsLayout,
  getCheckoutSeatsToSelect,
  getCheckoutSeatTypes,
  getCheckoutSelectedSeats,
  getTicketsCheckoutFirstStepConfig
} from '../../../reducers';
import {breakpoints} from '../../../utils/styles';
import SeatsSelectionCount from './SeatsSelectionCount';
import SeatsSelectionAuditoriumWithArrows from './SeatsSelectionAuditoriumWithArrows';
import './SeatsSelection.css';
import SeatsSelectionAuditoriumWithZoom from './SeatsSelectionAuditoriumWithZoom';
import ShowtimesSelector from '../ShowtimesSelector';

const SeatDimensions = `
    width: 1.5rem;
    height: auto;
    margin: 0.2rem;

    @media screen and (min-width: ${breakpoints.desktop}px) {
      margin: 0.15rem;
    }
`;

const SeatsReferences = styled.div`
    border-top: 1px solid var(--text-primary);
    margin-top: 3rem;
    padding-top: 1rem;
`;

const RowName = styled.span`
    font-size: 0.75em;
    line-height: 2.75em;
    width: 3.5em;
    display: inline-block;
    text-align: center;
    ${props => props.left ? 'padding-right: 2em;' : 'padding-left: 2em;'}
`;

const Seat = styled.span`
      cursor: pointer;
      position: relative;
`;

const SeatNumber = styled.span`
    font-size: 0.75em;
    line-height: 2.75em;
    text-align: center;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: ${props => props.selectable ? 'var(--text-primary)' : 'var(--page-bg)'};
`;

const SeatImg = styled.img`
    ${SeatDimensions}
`;

const SeatSpacer = styled.span`
    display: inline-block;
    ${SeatDimensions}
`;

const getSeatClasses = (seat, selected) => ['c-seats__seat'].concat(selected ?
    'is-selected'
    :
    getSeatClassesByStatus(seat.status)
).join(' ');

const getSeatClassesByStatus = status => {
  switch (status) {
    case 'E':
      return 'is-hide';
    case '1':
      return 'is-occupied';
    default:
      return '';
  }
};

const renderSeatTypesReferences = (configSeatTypes, sessionSeatTypes) => {
  const ids = Object.keys(configSeatTypes);

  return ids.map(key => {
    const props = configSeatTypes[key];
    key = key.split('_');
    const id = key[0];
    const status = key[1];

    return shouldShowSeatTypeReference(id, props, sessionSeatTypes) && renderSeatTypeReference(id, status, props)
  });
};

const shouldShowSeatTypeReference = (id, seatType, sessionSeatTypes) => {
  switch (seatType.show_reference) {
    case 'never': return false;
    case 'always': return true;
    case 'if-present': return sessionSeatTypes ? (sessionSeatTypes.indexOf(id) > -1) : false;
    default: return true;
  }
};

const renderSeatTypeReference = (id, status, props) => <span className="c-seats__seat"><SeatImg src={props.image}/> <small className="ml-2 text-secondary">{props.label}</small></span>;

class SeatsSelection extends React.Component {
  constructor(props) {
    super(props);

    this.renderRow = this.renderRow.bind(this);
    this.renderSeat = this.renderSeat.bind(this);
    this.renderSeatImage = this.renderSeatImage.bind(this);
  }

  render() {
    return (
        <React.Fragment>
          <StepHeader onPrevClick={this.props.prevStep} label="Selecciona tus asientos"/>

          {this.props.isFirstStep && <ShowtimesSelector currentSessionId={parseInt(this.props.session.id, 10)} />}

          {/* Mobile */}
          <div className="d-block d-sm-none">
            <SeatsSelectionAuditoriumWithZoom>
              {this.props.layout && this.props.layout.map(this.renderRow)}
            </SeatsSelectionAuditoriumWithZoom>
          </div>

          {/* Desktop */}
          <div className="d-none d-sm-block">
            <SeatsSelectionAuditoriumWithArrows>
              {this.props.layout && this.props.layout.map(this.renderRow)}
            </SeatsSelectionAuditoriumWithArrows>
          </div>

          <SeatsReferences className="c-seats c-seats--indicators mb-5">
            {renderSeatTypesReferences(this.props.seatTypesConfig, this.props.seatTypes)}
          </SeatsReferences>

          {!this.props.seatsPreSelected && !this.props.isFirstStep && (
            <SeatsSelectionCount total={this.props.seatsToSelect} selected={this.props.seats.length} />
          )}
        </React.Fragment>
    );
  }

  renderRow(row) {
    return (
      <div className="c-seats__row mb-1" key={row.name}>
        <RowName left>{row.name}</RowName>
        {Object.values(row.seats).reverse().map(this.renderSeat)}
        <RowName right>{row.name}</RowName>
      </div>
    );
  }

  renderSeat(seat) {
    return (
      <Seat className={getSeatClasses(seat, this.props.seats.indexOf(seat.id) > -1)}
            key={seat.id} onClick={() => this.props.seatSelect(seat)}>
        {this.renderSeatImage(seat)}
        {this.renderSeatNumber(seat)}
      </Seat>
    );
  }

  renderSeatImage(seat) {
    const seatConfig = this.props.seatTypesConfig[this.getSeatTypeString(seat)];

    if (seatConfig && seatConfig.image) {
      return <SeatImg src={seatConfig.image} />;
    } else {
      return <SeatSpacer/>;
    }
  }

  renderSeatNumber(seat) {
    const seatConfig = this.props.seatTypesConfig[this.getSeatTypeString(seat)];

    if (seatConfig && seatConfig.show_number) {
      return <SeatNumber selectable={seatConfig.selectable}>{seat.label}</SeatNumber>;
    } else {
      return null;
    }
  }

  getSeatTypeString(seat) {
    const parts = [seat.type];
    const isSelected = this.props.seats.indexOf(seat.id) > -1;

    if (isSelected) {
      parts.push('selected');
    } else {
      parts.push(seat.status);
    }

    return parts.join('_');
  }
}

const mapStateToProps = state => ({
  seatTypesConfig: getCheckoutSeatTypes(state),
  customPointer: state.checkoutData.data.custom_pointer,
  isFirstStep: 'seats' === getTicketsCheckoutFirstStepConfig(state),
  layout: getCheckoutSeatsLayout(state),
  seats: getCheckoutSelectedSeats(state),
  seatTypes: state.checkoutData.seatTypes,
  seatsPreSelected: areCheckoutSeatsPreSelected(state),
  seatsToSelect: getCheckoutSeatsToSelect(state),
  screenImage: state.checkoutData.data.screen_override || null,
});

const mapDispatchToProps = dispatch => ({
  seatSelect: seat => dispatch(seatSelect(seat)),
  prevStep: () => dispatch(prevStep()),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(SeatsSelection);